// Tells a tab that is already open that a newer build has been deployed.
//
// The app ships as plain static files with no bundler and no service worker.
// Firebase serves the HTML, JS and CSS with Cache-Control: no-cache, so a
// RELOAD always picks up the new code — but nothing tells a tab left open for
// hours that a reload is worth doing, and arbiters keep this app open for the
// length of a tournament. This polls version.txt (written at deploy time by
// scripts/stamp-version.mjs) and raises the banner in index.html when the value
// changes underneath us.
//
// It never reloads on its own. A forced reload during a round would throw away
// half-entered results, so the reload is always the user's click. Dismissing is
// remembered per version, so someone who wants to finish the round first is
// asked again only when a further deploy lands.
//
// Classic (non-module) script, like js/ui/dialog.js, so it can be dropped into
// any page with a <script> tag and needs nothing from the app's state.
(function () {
    'use strict';

    // Absolute: the marker lives at the site root, and the fetch must resolve
    // there no matter which path the page containing this script is served from.
    var VERSION_URL = '/version.txt';
    var POLL_MS = 5 * 60 * 1000;   // steady background poll
    var MIN_GAP_MS = 60 * 1000;    // floor between checks triggered by focus/reconnect

    // The version this tab is running. Established by the first successful
    // fetch rather than baked into the page, because there is no build step to
    // bake it in with — and index.html is served no-cache, so the file we fetch
    // moments after load is the one belonging to the code we just loaded.
    var loadedVersion = null;
    var dismissedVersion = null;
    var lastCheck = 0;
    var banner = null;

    function t(str) {
        return (typeof window.tr === 'function') ? window.tr(str) : str;
    }

    function fetchVersion() {
        // The query string defeats any intermediate proxy that ignores no-store.
        return fetch(VERSION_URL + '?t=' + Date.now(), { cache: 'no-store' })
            .then(function (res) { return res.ok ? res.text() : null; })
            .then(function (text) {
                if (text === null) return null;
                var value = text.trim();
                // A 404 page, a rewrite or a captive-portal login screen all
                // come back as HTML with a 200; only accept something that
                // looks like a stamp, or every hotel wifi becomes an "update".
                if (!value || value.length > 100 || value.indexOf('<') !== -1) return null;
                return value;
            })
            .catch(function () { return null; });  // offline, blocked, not deployed yet
    }

    // The build this tab is running, for anything that needs to report it rather
    // than act on it — the Contact form attaches it so a bug report says which
    // deploy it came from (js/ui/contact.js). Null until the first fetch lands,
    // and null forever offline or before the first deploy, so every reader must
    // treat it as optional.
    window.zugzwangBuild = function () { return loadedVersion; };

    function check(force) {
        var now = Date.now();
        if (!force && now - lastCheck < MIN_GAP_MS) return;
        if (!navigator.onLine) return;
        lastCheck = now;
        fetchVersion().then(function (version) {
            if (!version) return;
            if (loadedVersion === null) { loadedVersion = version; return; }
            if (version !== loadedVersion && version !== dismissedVersion) show(version);
        });
    }

    function show(version) {
        if (!banner) return;
        banner.dataset.version = version;
        banner.classList.add('active');
    }

    function hide() {
        if (!banner) return;
        dismissedVersion = banner.dataset.version || null;
        banner.classList.remove('active');
    }

    // The service worker belongs to a DEPLOY, not to a working copy. Its cache
    // name embeds the build stamp, and that stamp only changes when
    // scripts/stamp-version.mjs runs — which on a development machine is never.
    // So a worker registered against localhost caches the app once and then
    // serves those exact bytes for good: every later edit is invisible, and the
    // half-old/half-new page that results is a white screen no ordinary reload
    // can clear, only a cache-bypassing one. Development gets no worker.
    //
    // Loopback plus the private ranges: a phone pointed at the dev machine over
    // the LAN is reading that same working copy, and no production host ever
    // answers on one of these addresses.
    var isLocalHost = /^(localhost|127\.0\.0\.1|\[::1\]|10\.\d+\.\d+\.\d+|192\.168\.\d+\.\d+|172\.(1[6-9]|2\d|3[01])\.\d+\.\d+)$/
        .test(location.hostname);

    // Registers the service worker that makes the app open without a connection
    // (scripts/sw-template.js explains what it caches and why). Deliberately fire
    // and forget: /sw.js only exists after a deploy or a local `npm run
    // stamp-version`, and an app that cannot cache itself is still a working app.
    function registerServiceWorker() {
        if (!('serviceWorker' in navigator)) return;
        if (isLocalHost) { unregisterServiceWorker(); return; }
        navigator.serviceWorker.register('/sw.js').catch(function () {
            /* not generated yet, or blocked — offline support is a bonus, not a
               dependency, so there is nothing to report to the user here. */
        });
    }

    // Undo a worker a previous visit to this host left behind, and drop the
    // caches with it — unregistering alone leaves the stored responses in place
    // for the next worker to serve. Only ever runs on localhost, so it cannot
    // touch a real installation.
    function unregisterServiceWorker() {
        navigator.serviceWorker.getRegistrations().then(function (regs) {
            var hadWorker = regs.length > 0;
            return Promise.all(regs.map(function (reg) { return reg.unregister(); }))
                .then(function () {
                    if (!('caches' in window)) return false;
                    return caches.keys().then(function (names) {
                        return Promise.all(names
                            .filter(function (n) { return n.indexOf('zugzwang-') === 0; })
                            .map(function (n) { return caches.delete(n); }));
                    }).then(function () { return hadWorker; });
                });
        }).then(function (hadWorker) {
            // The page currently on screen was still served BY that worker, so
            // it can be the stale mixture this exists to get rid of. One reload,
            // once, now that nothing is left to serve it from the cache.
            if (hadWorker) location.reload();
        }).catch(function () { /* nothing to undo */ });
    }

    // A new build installs its worker and then WAITS, so the tab keeps running the
    // code it started with until the user says otherwise (a forced swap mid-round
    // is exactly what this whole file exists to avoid). Reloading is that moment:
    // wake the waiting worker first, and reload once it has taken control, or the
    // reload would just be served the old build out of the old cache again.
    //
    // Every branch ends in a reload. The handshake is an optimisation on an
    // ordinary reload, never a prerequisite for one — no worker, no waiting
    // update, or a browser that never fires controllerchange all fall through to
    // the same place, and the timeout makes sure of the last of those.
    function reloadOntoNewBuild() {
        if (!('serviceWorker' in navigator)) { location.reload(); return; }

        navigator.serviceWorker.getRegistration().then(function (reg) {
            if (!reg || !reg.waiting) { location.reload(); return; }

            var done = false;
            function go() {
                if (done) return;
                done = true;
                location.reload();
            }
            navigator.serviceWorker.addEventListener('controllerchange', go, { once: true });
            reg.waiting.postMessage({ type: 'SKIP_WAITING' });
            setTimeout(go, 2000);
        }).catch(function () { location.reload(); });
    }

    function init() {
        registerServiceWorker();

        banner = document.getElementById('update-banner');
        if (!banner) return;

        var reloadBtn = document.getElementById('update-banner-reload');
        var dismissBtn = document.getElementById('update-banner-dismiss');
        if (reloadBtn) {
            reloadBtn.addEventListener('click', function () {
                reloadBtn.disabled = true;
                reloadBtn.textContent = t('Updating…');
                reloadOntoNewBuild();
            });
        }
        if (dismissBtn) dismissBtn.addEventListener('click', hide);

        check(true);
        setInterval(function () { check(true); }, POLL_MS);

        // Coming back to the tab is the moment a stale one is most likely to be
        // used again, and reconnecting is the moment a missed poll can be
        // retried — both throttled by MIN_GAP_MS so a burst of tab switching
        // does not turn into a burst of requests.
        document.addEventListener('visibilitychange', function () {
            if (!document.hidden) check(false);
        });
        window.addEventListener('online', function () { check(false); });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
